import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Trash2, Dog, Clock, X } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';

const NotificationDropdown = ({ darkMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { notifications, unreadCount, markAllAsRead, markAsRead, clearAll } = useNotifications();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Format timestamp to relative time
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    // Navigate to media gallery with report ID
    setIsOpen(false);
    navigate('/media-gallery', { state: { reportId: notification.id } });
  };

  // Dropdown content
  const dropdownContent = (
    <>
      {/* Overlay for mobile/tablet */}
      <div
        className="fixed inset-0 bg-black/40 z-[100] lg:hidden"
        onClick={() => setIsOpen(false)}
      />

      {/* Dropdown Panel */}
      <div
        ref={dropdownRef}
        className={`
          fixed lg:absolute z-[101]

          /* Mobile: Bottom sheet */
          inset-x-0 bottom-0
          w-full max-h-[85vh]
          rounded-t-2xl

          /* Tablet & Desktop: Dropdown */
          lg:inset-auto lg:bottom-auto lg:right-0 lg:top-full lg:mt-2
          lg:w-96 lg:max-h-[500px]
          lg:rounded-xl

          shadow-2xl border overflow-hidden
          ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
        `}
      >
        {/* Drag handle for mobile */}
        <div className="lg:hidden flex justify-center py-2">
          <div className={`w-10 h-1 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
        </div>

        {/* Header */}
        <div className={`px-4 py-3 border-b flex items-center justify-between ${
          darkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center gap-2">
            <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Notifications
            </h3>
            {unreadCount > 0 && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-600'
              }`}>
                {unreadCount} new
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className={`p-1.5 rounded-lg text-xs flex items-center gap-1 transition-colors ${
                  darkMode
                    ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200'
                    : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                }`}
                title="Mark all as read"
              >
                <Check size={14} />
              </button>
            )}
            {notifications.length > 0 && (
              <button
                onClick={clearAll}
                className={`p-1.5 rounded-lg text-xs flex items-center gap-1 transition-colors ${
                  darkMode
                    ? 'hover:bg-gray-700 text-gray-400 hover:text-red-400'
                    : 'hover:bg-gray-100 text-gray-500 hover:text-red-500'
                }`}
                title="Clear all"
              >
                <Trash2 size={14} />
              </button>
            )}
            {/* Close button */}
            <button
              onClick={() => setIsOpen(false)}
              className={`p-1.5 rounded-lg lg:hidden transition-colors ${
                darkMode
                  ? 'hover:bg-gray-700 text-gray-400'
                  : 'hover:bg-gray-100 text-gray-500'
              }`}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Notification List */}
        <div className="overflow-y-auto max-h-[60vh] lg:max-h-80">
          {notifications.length === 0 ? (
            <div className={`px-4 py-8 text-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <Bell size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No notifications yet</p>
              <p className="text-xs mt-1">New reports will appear here</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`px-4 py-3 border-b cursor-pointer transition-colors ${
                  darkMode
                    ? `border-gray-700 ${notification.read ? 'bg-gray-800' : 'bg-gray-700/50 hover:bg-gray-700'}`
                    : `border-gray-100 ${notification.read ? 'bg-white' : 'bg-blue-50/50 hover:bg-gray-50'}`
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className={`p-2 rounded-lg flex-shrink-0 ${
                    notification.data?.dogType === 'Stray'
                      ? darkMode ? 'bg-orange-900/30 text-orange-400' : 'bg-orange-100 text-orange-600'
                      : darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-600'
                  }`}>
                    <Dog size={16} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className={`text-sm font-medium truncate ${
                        darkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {notification.title}
                      </p>
                      {!notification.read && (
                        <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                      )}
                    </div>
                    <p className={`text-sm truncate ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {notification.message}
                    </p>
                    <div className={`flex items-center gap-1 mt-1 text-xs ${
                      darkMode ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                      <Clock size={10} />
                      <span>{formatTime(notification.timestamp)}</span>
                      {notification.data?.uploadedBy && (
                        <>
                          <span className="mx-1">•</span>
                          <span className="truncate">{notification.data.uploadedBy}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className={`px-4 py-2 border-t text-center ${
            darkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Showing last {notifications.length} notifications
            </p>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="relative" ref={buttonRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-lg transition-all ${
          darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
        } ${isOpen ? (darkMode ? 'bg-gray-800' : 'bg-gray-100') : ''}`}
      >
        <Bell size={18} className={darkMode ? 'text-gray-300' : 'text-gray-600'} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] rounded-full min-w-[16px] h-4 flex items-center justify-center font-medium px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Render dropdown - portal for mobile/tablet, inline for desktop */}
      {isOpen && (
        typeof window !== 'undefined' && window.innerWidth < 1024
          ? createPortal(dropdownContent, document.body)
          : dropdownContent
      )}
    </div>
  );
};

export default NotificationDropdown;
